import CashIcon from 'assets/icons/cash-icon.svg?component'
import CashStackIcon from 'assets/icons/cash-stack-icon.svg?component'
import ClockIcon from 'assets/icons/clock-icon.svg?component'
import PercentageIcon from 'assets/icons/percentage-icon.svg?component'
import StarIcon from 'assets/icons/star-icon.svg?component'
import Alert from 'components/UI/Alert'
import Button from 'components/UI/Buttons/Button'
import Typography from 'components/UI/Typography'
import { useAppSelector } from 'hooks'
import { useCreditDecision } from 'hooks/useCreditDecision'
import { DecisionSubViews } from 'pages/Decision/constants'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { loanParamsSelector, programSelector } from 'redux/store/slices/decisionSlice'
import { getLoanInformation } from 'redux/store/slices/loanSlice'
import tw from 'twin.macro'
import { numberWithSpaces } from 'utils'
import LoanCalculator from 'utils/loan-calculator'
import { getLoanPurposeText, getProgramName } from 'utils/translations'
import { isScoringOptionsArray } from 'utils/type-guards'

import LoanConditions from './LoanConditions'

const LoanConditionsWrapper = () => {
  // Tools
  const { t } = useTranslation()

  // Selectors
  const selectedProgram = useAppSelector(programSelector)
  const { loanRepaymentMethod } = useAppSelector(getLoanInformation)
  const { amount, period } = useAppSelector(loanParamsSelector)

  // Custom hooks
  const { selectedSubProgram, prepareLoanParamsView, onSubViewChange } = useCreditDecision()

  // Computed Values
  const matchedOption = useMemo(() => {
    if (!selectedSubProgram || !period) return
    if (!isScoringOptionsArray(selectedSubProgram)) return
    return selectedSubProgram.options.find(option => option.credit_params.period === +period)
  }, [period, selectedSubProgram])

  const interestRate = useMemo(() => {
    if (!selectedSubProgram) return
    if (isScoringOptionsArray(selectedSubProgram)) {
      if (!matchedOption) return
      return matchedOption.credit_params.interest_rate
    }
    return selectedSubProgram.options.credit_params.interest_rate
  }, [matchedOption, selectedSubProgram])

  const monthlyPayment = useMemo(() => {
    if (!amount || !period || !interestRate || !selectedSubProgram || !loanRepaymentMethod) return
    const isDummyProgram = Object.keys(selectedSubProgram.options).length === 0
    if (isDummyProgram) return

    return new LoanCalculator(+amount, +period, +interestRate, loanRepaymentMethod).getCalcResults().monthlyPayment
  }, [amount, interestRate, loanRepaymentMethod, period, selectedSubProgram])

  const loanConditions = useMemo(() => {
    const conditions = [
      {
        caption: t('loan-conditions.loan-amount', { ns: 'common' }),
        body: `${numberWithSpaces(amount)} ₸`,
        icon: <CashStackIcon />
      },
      {
        caption: t('loan.period', { ns: 'common' }),
        body: t('calculator.period-limit', { ns: 'common', period }),
        icon: <ClockIcon />
      },
      {
        caption: t('loan.interest-rate', { ns: 'common' }),
        body: `${interestRate} %`,
        icon: <PercentageIcon />
      },
      {
        caption: t('loan.monthly-payment', { ns: 'common' }),
        body: `${numberWithSpaces(monthlyPayment)} ₸`,
        icon: <CashIcon />
      },
      {
        caption: t('programs.title', { ns: 'common' }),
        body: t(getProgramName(selectedProgram), { ns: 'common' }),
        icon: <StarIcon />
      }
    ]

    if (selectedSubProgram?.with_pledges) {
      return conditions
    }

    return [
      ...conditions,
      {
        caption: t('flow-goal'),
        body: t(getLoanPurposeText(selectedSubProgram?.loan_purpose_verbose), { ns: 'common' }),
        icon: <CashIcon />
      }
    ]
  }, [
    amount,
    interestRate,
    monthlyPayment,
    period,
    selectedProgram,
    selectedSubProgram?.loan_purpose_verbose,
    selectedSubProgram?.with_pledges,
    t
  ])

  // Handlers
  const handleGoBack = () => {
    if (!selectedProgram || !selectedSubProgram) return

    if (selectedSubProgram.with_pledges) return onSubViewChange(DecisionSubViews['CHOOSE-LOAN-PARAMS'])
    prepareLoanParamsView(selectedProgram)
  }

  return (
    <LoanConditions
      twStyle={tw`mt-3 lg:mt-5`}
      conditions={loanConditions}
      isConditionsChangeable
      conditionsStyle={selectedSubProgram?.with_pledges ? tw`border-b-0` : undefined}
      header={
        selectedSubProgram?.with_pledges ? null : (
          <div tw='p-5'>
            <Button variant='selector' twStyle={tw`w-full max-w-none`}>
              <Typography variant='body' text weight='bold'>
                до {numberWithSpaces(Number(selectedSubProgram?.max_loan_amount).toFixed(0))} тг
              </Typography>
              <Typography variant='caption'>
                {t(getLoanPurposeText(selectedSubProgram?.loan_purpose_verbose), { ns: 'common' })}
              </Typography>
            </Button>
          </div>
        )
      }
      onConditionChangeClick={handleGoBack}
    >
      {selectedSubProgram?.with_pledges ? (
        <Alert twStyle={tw`mx-4`}>
          <Typography variant='subbody'>{t('15-days')}</Typography>
        </Alert>
      ) : null}
    </LoanConditions>
  )
}

export default LoanConditionsWrapper
